import { storeToRefs } from "pinia";
import { useAcl } from "vue-simple-acl";
import { useFetchPage } from "~/core/composables/useFetchPage";
import { SearchMode, useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.type";
import type { UseFetchResponse } from "~/core/models/UseFetchResponse.interface";
import {
  Filters,
  type FiltersSelection,
  FiltersType,
  OPERATOR_RSQL_ILIKE,
} from "~/core/models/filters";
import { fetchAllPaginatedList } from "~/core/utils/fetch";
import filtersUtils from "~/core/utils/filters.utils";
import type { Sector } from "~/modules/sector/models/Sector.interface";
import type { SelectedItemType } from "~/modules/sector/models/SelectedItem.type";
import { useSectorStore } from "~/modules/sector/stores/useSectorStore";
import type { GroupsUserV2 } from "~/modules/user/models/GroupsUserV2.interface";
import userGroupService from "~/modules/user/services/userGroup.service";
import { SectorResource } from "~/types";

const { can } = useAcl();

interface UseFetchUserGroupsParameters extends UseFetchParameters {
  outSector?: boolean;
}

interface UseFetchUserGroupsResponse extends UseFetchResponse<GroupsUserV2> {
  isLoadingAllGroups: Ref<boolean | undefined>;
  fetchAll: () => Promise<GroupsUserV2[] | LxcError>;
  resultsFetchAll: Ref<GroupsUserV2[]>;
  errorFetchAll: globalThis.Ref<LxcError | undefined, LxcError | undefined>;
  getDescription: (item?: SelectedItemType | undefined) => string;
  getItemIds: (items: SelectedItemType[]) => string[];
  canManageItems: (sector: Sector) => boolean;
  itemType: SectorResource;
}

export function useFetchUserGroups({
  organizationCode,
  sectorCode,
  outSector,
}: UseFetchUserGroupsParameters): UseFetchUserGroupsResponse {
  const itemType = SectorResource.USER_GROUP;
  const isLoadingAllGroups: Ref<boolean | undefined> = ref();

  let service =
    userGroupService.getOrganizationUserGroups.bind(userGroupService);

  if (sectorCode) {
    if (outSector) {
      service =
        userGroupService.getOrganizationSectorUnassignedUserGroups.bind(
          userGroupService,
        );
    } else {
      service =
        userGroupService.getOrganizationSectorUserGroups.bind(userGroupService);
    }
  }

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.NAME,
      {
        key: "name",
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const resultsFetchAll: Ref<GroupsUserV2[]> = ref([]);
  const errorFetchAll: Ref<LxcError | undefined> = ref();

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    SearchMode.FILTER_SEARCH,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPage({
    organizationCode,
    sectorCode,
    service,
    searchParams,
    sort,
    useQueryParametersForPagination: false,
  });

  function getDescription(item?: SelectedItemType): string {
    return (item as GroupsUserV2)?.name ?? "";
  }

  function canManageItems(sector: Sector) {
    const { availableSectors } = storeToRefs(useSectorStore());
    return (
      can([ACLRoles.CAN_READ_SECTOR, ACLRoles.CAN_WRITE_USER]) &&
      availableSectors.value
        .map((availableSector) => availableSector.code)
        .includes(sector.code)
    );
  }

  function getItemIds(items: SelectedItemType[]): string[] {
    return items.map((item) => (item as GroupsUserV2).name);
  }

  async function fetchAll(): Promise<GroupsUserV2[] | LxcError> {
    isLoadingAllGroups.value = true;
    const response = await fetchAllPaginatedList<GroupsUserV2>(
      (page: number, pageSize: number) =>
        service({
          page,
          pageSize,
          organizationCode,
          searchParams: searchParams.value,
        }),
    );
    if (LxcError.check(response)) {
      errorFetchAll.value = response;
    } else {
      resultsFetchAll.value = response;
    }
    isLoadingAllGroups.value = false;
    return response;
  }

  return {
    isLoading,
    isLoadingAllGroups,
    results,
    error,
    filters,
    fetchData,
    fetchAll,
    resultsFetchAll,
    errorFetchAll,
    setFilter,
    onSortChange,
    getDescription,
    getItemIds,
    search,
    onSearch,
    canManageItems,
    itemType,
  };
}
